import { useRef, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';

/**
 * Copies text to the clipboard. The Clipboard API needs a secure context (https); in the
 * local network (plain http) the older selection-based copy is used.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall back below.
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  area.remove();
  return ok;
}

/** A read-only text (e.g. a one-time link) with a copy button next to it. */
export function CopyField(props: { value: string; label: string }) {
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const copy = async () => {
    const ok = await copyText(props.value);
    setState(ok ? 'copied' : 'failed');
    if (!ok) input.current?.select();
  };

  return (
    <div class="copy-field">
      <input
        ref={input}
        value={props.value}
        readOnly
        aria-label={props.label}
        spellcheck={false}
        onFocus={(e) => e.currentTarget.select()}
      />
      <button type="button" class={`btn btn-small ${state === 'copied' ? 'btn-success' : 'btn-primary'}`} onClick={() => void copy()}>
        {state === 'copied' ? `✓ ${t.admin.copied}` : `⧉ ${t.admin.copy}`}
      </button>
      {state === 'failed' && (
        <p class="error" role="alert">
          {t.admin.copyFailed}
        </p>
      )}
    </div>
  );
}
